import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Help,
  ExpandMore,
  Email,
  Send,
  AccessTime,
  MenuBook,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'medium',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const theme = useTheme();

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');
    setSubmitting(true);
    try {
      const token = getAuthToken();
      const response = await fetch('/api/help/support-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(contactForm),
      });
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to submit support ticket'));
      }
      const json = await response.json().catch(() => ({}));
      setSubmitSuccess(json.message || 'Support ticket submitted successfully.');
      setContactForm({
        name: '',
        email: '',
        subject: '',
        message: '',
        priority: 'medium',
      });
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to submit support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactChange = (field: string, value: string) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const faqData: FAQItem[] = [
    {
      question: "How do I create a new case?",
      answer: "To create a new case, go to Cases and click New Case Intake. Complete the intake wizard with client, matter type, and required questionnaire fields.",
      category: "Cases"
    },
    {
      question: "How can I upload documents?",
      answer: "Go to the Documents section and click 'Upload Document'. You can drag and drop files or click to browse. Supported formats include PDF, DOC, DOCX, and image files.",
      category: "Documents"
    },
    {
      question: "How do I generate invoices?",
      answer: "Navigate to the Billing section, select a client, and click 'Create Invoice'. Fill in the service details, rates, and any applicable taxes or discounts.",
      category: "Billing"
    },
    {
      question: "Can I customize the dashboard?",
      answer: "Yes! Go to Settings > Appearance to customize themes, colors, and layout options. You can also configure notification preferences in the Settings section.",
      category: "Settings"
    },
    {
      question: "How do I manage user permissions?",
      answer: "Administrators can manage user permissions in the Security section. Navigate to Security > User Management to add, edit, or remove users and their access levels.",
      category: "Security"
    },
    {
      question: "How do I export reports?",
      answer: "In the Reports section, select the report type and date range, then click the 'Export' button. Reports can be downloaded as PDF or Excel files.",
      category: "Reports"
    },
    {
      question: "What are the system requirements?",
      answer: "The application works on all modern browsers including Chrome, Firefox, Safari, and Edge. For optimal performance, we recommend using the latest browser versions.",
      category: "Technical"
    },
    {
      question: "How do I reset my password?",
      answer: "Click on 'Forgot Password' on the login page, enter your email address, and follow the instructions sent to your email to reset your password.",
      category: "Account"
    }
  ];

  const supportHours = [
    { day: 'Weekdays', hours: 'Support tickets are reviewed during business hours' },
    { day: 'Weekends', hours: 'Urgent issues: submit a ticket and we will follow up' },
  ];

  const contactMethods = [
    {
      icon: <Email />,
      title: 'Support ticket',
      description: 'Use the Contact Support form on this page',
      response: 'Replies go to the email you provide'
    },
  ];

  const renderFAQ = () => {
    const categories = [...new Set(faqData.map(item => item.category))];
    
    return (
      <Box>
        <Typography variant="h5" gutterBottom component="span">
          Frequently Asked Questions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} component="span">
          Find answers to common questions about using the Legal Dashboard
        </Typography>
        
        {categories.map((category) => (
          <Box key={category} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }} component="span">
              {category}
            </Typography>
            {faqData
              .filter(item => item.category === category)
              .map((item, index) => (
                <Accordion
                  key={index}
                  expanded={expanded === `${category}-${index}`}
                  onChange={handleAccordionChange(`${category}-${index}`)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" component="span">
                      {item.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" component="span">
                      {item.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
          </Box>
        ))}
      </Box>
    );
  };

  const renderContactForm = () => {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom component="span">
            Contact Support
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} component="span">
            Need help? Submit a support ticket and we'll get back to you as soon as possible.
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          )}
          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSubmitSuccess('')}>
              {submitSuccess}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleContactSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  value={contactForm.name}
                  onChange={(e) => handleContactChange('name', e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => handleContactChange('email', e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            
            <TextField
              label="Subject"
              value={contactForm.subject}
              onChange={(e) => handleContactChange('subject', e.target.value)}
              fullWidth
              required
            />
            
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={contactForm.priority}
                label="Priority"
                onChange={(e) => handleContactChange('priority', e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Message"
              multiline
              rows={4}
              value={contactForm.message}
              onChange={(e) => handleContactChange('message', e.target.value)}
              fullWidth
              required
            />
            
            <Button
              type="submit"
              variant="contained"
              startIcon={<Send />}
              disabled={submitting}
              sx={{ alignSelf: 'flex-start' }}
            >
              {submitting ? 'Submitting…' : 'Submit Ticket'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderContactInfo = () => {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom component="span">
            Contact Information
          </Typography>
          
          <List>
            {contactMethods.map((method, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  {method.icon}
                </ListItemIcon>
                <ListItemText
                  primary={method.title}
                  secondary={
                    <Box component="span">
                      <Typography variant="body2" component="span">
                        {method.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" component="span">
                        {method.response}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6" gutterBottom component="span">
            Support Hours
          </Typography>
          <List>
            {supportHours.map((schedule, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <AccessTime />
                </ListItemIcon>
                <ListItemText
                  primary={schedule.day}
                  secondary={schedule.hours}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  const renderResources = () => {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom component="span">
            Helpful Resources
          </Typography>
          <Typography variant="body2" color="text.secondary" component="span">
            Use the FAQ on this page for cases, documents, billing, and account access.
            Submit a support ticket if you need help from the ArgisLegal team.
          </Typography>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Help sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Help & Support
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" component="span">
          Need immediate assistance? Our support team is available during business hours. 
          For urgent issues, please call our support line.
        </Typography>
      </Alert>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <MenuBook color="primary" sx={{ fontSize: 40 }} />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h6" component="span" display="block">User&apos;s Guide</Typography>
            <Typography variant="body2" color="text.secondary" component="span">
              Step-by-step instructions for every feature — firm setup, cases, activity log, documents and court forms, billing and reports — filtered to your role.
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => navigate('/help/guide')}>Open the User&apos;s Guide</Button>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          {renderFAQ()}
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {renderContactInfo()}
            {renderResources()}
          </Box>
        </Grid>
        
        <Grid item xs={12}>
          {renderContactForm()}
        </Grid>
      </Grid>
    </Box>
  );
};

export default HelpPage; 